import { useEffect, useState } from 'react'

import CostsService from '../../services/CostsService'
import PaymentService from '../../services/PaymentService'

/**
 * True once `ref` is really on screen, and from then on.
 *
 * The Costs section sits under the login form: most visitors sign in without scrolling,
 * so its three requests start only when the section is being looked at. It starts right
 * at the fold (a few pixels of it can already be visible), hence the root shrunk at the
 * bottom and a real overlap (ratio > 0, not mere adjacency) as the trigger. Where the
 * section opens the page (/costs with a session, the recharging station) it loads at
 * once. Without IntersectionObserver (old browsers, tests) it is true at once.
 */
export const useNearViewport = (ref, margin = '0px 0px -80px 0px') => {
  const [near, setNear] = useState(
    () => typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function'
  )

  useEffect(() => {
    if (near || !ref.current) return undefined
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting && e.intersectionRatio > 0)) {
          setNear(true)
          observer.disconnect()
        }
      },
      { rootMargin: margin, threshold: [0, 0.01] }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [near, ref, margin])

  return near
}

const idle = { status: 'idle', data: null }

// Each source loads on its own: the rate card failing must not hide the plans.
const SOURCES = {
  priceList: () => CostsService.getPriceList(),
  packages: () => PaymentService.getPackages(),
  rates: () => CostsService.getRates(),
}

/**
 * The three public sources of the Costs section: {priceList, packages, rates}, each
 * {status: 'idle'|'loading'|'ready'|'error', data}. Starts when `enabled` turns true.
 */
const useCostsData = (enabled) => {
  const [state, setState] = useState({ priceList: idle, packages: idle, rates: idle })

  useEffect(() => {
    if (!enabled) return undefined
    let alive = true
    Object.entries(SOURCES).forEach(([name, load]) => {
      setState((s) => ({ ...s, [name]: { status: 'loading', data: null } }))
      load()
        .then((r) => {
          if (alive) setState((s) => ({ ...s, [name]: { status: 'ready', data: r?.data ?? null } }))
        })
        .catch(() => {
          if (alive) setState((s) => ({ ...s, [name]: { status: 'error', data: null } }))
        })
    })
    return () => {
      alive = false
    }
  }, [enabled])

  return state
}

export default useCostsData
